import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  RefreshCw,
  Search,
  Database,
  Save,
  Download,
  ChevronLeft,
  ChevronRight,
  History,
  Share2,
} from "lucide-react";

import {
  listMemoryCollections,
  queryMemory,
  updateMemoryRecord,
  fetchMemoryNeighbors,
  promoteMemoryRecord,
  type MemoryCollection,
  type MemoryRecord,
  type QueryMemoryPayload,
} from "@/api/memory";
import { FilterBuilder, type FilterRow } from "@/components/memory/FilterBuilder";
import { useAuth } from "@/lib/authContext";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { Button } from "@/components/ui/button";
import { useHasCapability } from "@/hooks/useCapabilities";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";

function tryParseJson(value: string): Record<string, unknown> | null {
  if (!value.trim()) {
    return null;
  }
  return JSON.parse(value);
}

type SavedQuery = {
  id: string;
  name: string;
  payload: Omit<QueryMemoryPayload, "collection">;
  queryText: string;
  metadataFilters: FilterRow[];
  metadataJsonText: string;
  documentFilter: string;
  includeEmbeddings: boolean;
  pageSize: number;
  collection: string;
  useSemanticQuery?: boolean;
  useMetadataFilters?: boolean;
  signature: string;
};

const RECENTS_KEY = "memoryBrowser:recentQueries";
const STATUS_PRESETS = ["pending", "open", "confirmed", "denied"];
const REGION_PRESETS = ["West", "Midwest", "South", "Northeast", "Europe"];
const TAG_PRESETS = ["bandsintown", "memory", "priority", "crm_contact", "lead_candidate"];

type RevisionEntry = {
  timestamp: string;
  user?: string;
  summary?: string;
  changes?: Record<string, unknown>;
};

export default function MemoryBrowserPage() {
  const location = useLocation();
  const { profile } = useAuth();
  const { openUpgradeModal } = useUpgradeModal();
  const canUseMemoryBrowser = useHasCapability("canUseMemoryBrowser");
  const canUseMemoryEdit = useHasCapability("canUseMemoryEdit");
  const canUseMemoryPromotion = useHasCapability("canUseMemoryPromotion");
  const canUseMemoryNeighbors = useHasCapability("canUseMemoryNeighbors");
  const canUseMemoryFiltersAdvanced = useHasCapability("canUseMemoryFiltersAdvanced");
  const [collections, setCollections] = useState<MemoryCollection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);

  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);

  const [queryText, setQueryText] = useState("");
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
  const [metadataFilter, setMetadataFilter] = useState("");
  const [documentFilter, setDocumentFilter] = useState("");
  const [useSemanticQuery, setUseSemanticQuery] = useState(true);
  const [useMetadataFilters, setUseMetadataFilters] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [includeEmbeddings, setIncludeEmbeddings] = useState(false);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState<number | null>(null);
  const [lastPayload, setLastPayload] = useState<QueryMemoryPayload | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<MemoryRecord[]>([]);
  const [recentQueries, setRecentQueries] = useState<SavedQuery[]>([]);
  const [selectedRecentId, setSelectedRecentId] = useState<string>("");

  const [selectedRecord, setSelectedRecord] = useState<MemoryRecord | null>(null);
  const [metadataDraft, setMetadataDraft] = useState("");
  const [documentDraft, setDocumentDraft] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [neighbors, setNeighbors] = useState<MemoryRecord[]>([]);
  const [neighborsLoading, setNeighborsLoading] = useState(false);
  const [neighborsError, setNeighborsError] = useState<string | null>(null);
  const [promoteStatus, setPromoteStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [promoteMessage, setPromoteMessage] = useState<string | null>(null);
  const handleUpgradeError = (err: unknown) => {
    if (isRequiresProError(err)) {
      openUpgradeModal();
      return true;
    }
    return false;
  };

  const editorName = useMemo(() => {
    const profileRecord = profile as Record<string, unknown> | null;
    const readField = (field: string) => {
      const value = profileRecord?.[field];
      return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
    };
    return (
      readField("name") ||
      readField("email") ||
      readField("preferred_username") ||
      "Memory Browser"
    );
  }, [profile]);

  const generateRowId = () =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENTS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as SavedQuery[];
        setRecentQueries(parsed);
        if (parsed.length > 0) {
          restoreSavedQuery(parsed[0]);
          setSelectedRecentId(parsed[0].id);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadId = params.get("lead");
    const contactId = params.get("contact");
    const queryTextParam = params.get("q");
    if (!leadId && !contactId && !queryTextParam) {
      return;
    }
    if (queryTextParam) {
      setQueryText(queryTextParam);
    }
    const presetFilters: FilterRow[] = [];
    if (leadId) {
      presetFilters.push({ id: generateRowId(), field: "__custom__", customField: "lead_id", value: leadId });
    }
    if (contactId) {
      presetFilters.push({
        id: generateRowId(),
        field: "__custom__",
        customField: "contact_id",
        value: contactId,
      });
    }
    if (presetFilters.length) {
      setFilterRows(presetFilters);
    }
    const metadataFilters: Record<string, string> = {};
    if (leadId) metadataFilters.lead_id = leadId;
    if (contactId) metadataFilters.contact_id = contactId;
    if (Object.keys(metadataFilters).length) {
      setMetadataFilter(JSON.stringify(metadataFilters, null, 2));
    }
  }, [location.search]);

  async function loadCollections() {
    setCollectionsLoading(true);
    setCollectionsError(null);
    try {
      const payload = await listMemoryCollections();
      setCollections(payload);
      if (!selectedCollection && payload.length > 0) {
        setSelectedCollection(payload[0]?.name ?? null);
      }
    } catch (err) {
      setCollectionsError(err instanceof Error ? err.message : "Failed to load collections");
    } finally {
      setCollectionsLoading(false);
    }
  }

  useEffect(() => {
    void loadCollections();
  }, []);

  useEffect(() => {
    if (!selectedRecord) return;
    setMetadataDraft(
      selectedRecord.metadata ? JSON.stringify(selectedRecord.metadata, null, 2) : "",
    );
    setDocumentDraft(selectedRecord.document ?? "");
    setSaveStatus("idle");
    setSaveMessage(null);
  }, [selectedRecord?.id]);

  useEffect(() => {
    if (!canUseMemoryNeighbors || !selectedCollection || !selectedRecord) {
      setNeighbors([]);
      setNeighborsLoading(false);
      setNeighborsError(null);
      return;
    }
    setNeighborsLoading(true);
    setNeighborsError(null);
    fetchMemoryNeighbors(selectedCollection, selectedRecord.id, { limit: 5 })
      .then((response) => {
        setNeighbors(response.results);
      })
      .catch((err) => {
        if (handleUpgradeError(err)) {
          setNeighbors([]);
          return;
        }
        setNeighborsError(err instanceof Error ? err.message : "Unable to load neighbors");
        setNeighbors([]);
      })
      .finally(() => {
        setNeighborsLoading(false);
      });
  }, [canUseMemoryNeighbors, selectedCollection, selectedRecord?.id]);

  const metadataFilterFromBuilder = useMemo(() => {
    const built: Record<string, unknown> = {};
    filterRows.forEach((row) => {
      const key =
        row.field === "__custom__" ? row.customField?.trim() || "" : row.field?.trim() || "";
      if (!key || !row.value.trim()) return;
      built[key] = row.value.trim();
    });
    return Object.keys(built).length ? built : null;
  }, [filterRows]);

  const handlePresetFilter = (field: string, value: string) => {
    if (!value.trim()) return;
    setFilterRows((prev) => {
      const exists = prev.some(
        (row) => row.field === field && row.value.toLowerCase() === value.toLowerCase(),
      );
      if (exists) {
        return prev;
      }
      return [
        ...prev,
        {
          id: generateRowId(),
          field,
          value,
        },
      ];
    });
  };

  const handleSearch = async () => {
    if (!canUseMemoryBrowser) {
      openUpgradeModal();
      return;
    }
    if (!selectedCollection) {
      setSearchError("Select a collection to query.");
      return;
    }
    let parsedMetadataJSON: Record<string, unknown> | null = null;
    let parsedDocumentFilter: Record<string, unknown> | null = null;
    try {
      parsedMetadataJSON = metadataFilter.trim() ? tryParseJson(metadataFilter) : null;
    } catch (err) {
      setSearchError(
        err instanceof Error ? `Metadata filter error: ${err.message}` : "Invalid metadata JSON",
      );
      return;
    }
    try {
      parsedDocumentFilter = documentFilter.trim() ? tryParseJson(documentFilter) : null;
    } catch (err) {
      setSearchError(
        err instanceof Error ? `Document filter error: ${err.message}` : "Invalid document JSON",
      );
      return;
    }
    const trimmedQuery = queryText.trim();
    const effectiveQuery = useSemanticQuery && trimmedQuery ? trimmedQuery : null;
    const builderFilters = useMetadataFilters ? metadataFilterFromBuilder : null;
    const jsonFilters = useMetadataFilters ? parsedMetadataJSON : null;
    const mergedMetadata =
      builderFilters && jsonFilters
        ? { ...jsonFilters, ...builderFilters }
        : builderFilters || jsonFilters;

    if (!effectiveQuery && !mergedMetadata && !parsedDocumentFilter) {
      setSearchError(
        "Enable the semantic prompt or add metadata/document filters before searching.",
      );
      return;
    }
    setPage(1);
    await runSearch({
      collection: selectedCollection,
      query: effectiveQuery,
      where: mergedMetadata,
      where_document: parsedDocumentFilter,
      include_embeddings: includeEmbeddings,
      page: 1,
      page_size: pageSize,
      include_total: true,
    });
  };

  const runSearch = async (payload: QueryMemoryPayload) => {
    setSearching(true);
    setSearchError(null);
    try {
      const response = await queryMemory(payload);
      setResults(response.results);
      setSelectedRecord(response.results[0] ?? null);
      setTotalResults(typeof response.total === "number" ? response.total : response.results.length);
      setPage(response.page);
      setLastPayload(payload);
      persistRecentQuery(payload);
    } catch (err) {
      if (handleUpgradeError(err)) {
        return;
      }
      setSearchError(err instanceof Error ? err.message : "Unable to run query");
    } finally {
      setSearching(false);
    }
  };

  const persistRecentQuery = (payload: QueryMemoryPayload) => {
    if (!canUseMemoryBrowser) {
      return;
    }
    const collectionName = payload.collection || selectedCollection;
    if (!collectionName) {
      return;
    }
    if ((payload.page ?? 1) > 1) {
      return;
    }
    const signature = JSON.stringify({
      collection: collectionName,
      query: queryText.trim(),
      metadataFilters: filterRows.map((row) => ({
        field: row.field,
        customField: row.customField,
        value: row.value,
      })),
      metadataJson: metadataFilter,
      documentFilter,
      includeEmbeddings,
      useSemanticQuery,
      useMetadataFilters,
      pageSize,
    });
    const safeFilters = filterRows.map((row) => ({
      ...row,
      id: generateRowId(),
    }));
    const { collection, ...restPayload } = payload;
    const entry: SavedQuery = {
      id: generateRowId(),
      name: `${collectionName} · ${new Date().toLocaleTimeString()}`,
      collection: collectionName,
      payload: restPayload,
      queryText,
      metadataFilters: safeFilters,
      metadataJsonText: metadataFilter,
      documentFilter,
      includeEmbeddings,
      pageSize,
      useSemanticQuery,
      useMetadataFilters,
      signature,
    };
    setRecentQueries((prev) => {
      const filtered = prev.filter((item) => item.signature !== signature);
      const merged = [entry, ...filtered].slice(0, 5);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(merged));
      setSelectedRecentId(entry.id);
      return merged;
    });
  };

  const restoreSavedQuery = (saved: SavedQuery) => {
    setSelectedCollection(saved.collection);
    setQueryText(saved.queryText);
    setFilterRows(saved.metadataFilters.map((row) => ({ ...row, id: generateRowId() })));
    setMetadataFilter(saved.metadataJsonText || "");
    setDocumentFilter(saved.documentFilter);
    setIncludeEmbeddings(saved.includeEmbeddings);
    setPageSize(saved.pageSize);
    setUseSemanticQuery(saved.useSemanticQuery ?? true);
    setUseMetadataFilters(saved.useMetadataFilters ?? true);
  };

  const handleSelectRecord = (record: MemoryRecord) => {
    setSelectedRecord(record);
  };

  const handleSaveRecord = async () => {
    if (!selectedCollection || !selectedRecord) {
      return;
    }
    if (!canUseMemoryEdit) {
      openUpgradeModal();
      return;
    }
    let parsedMetadata: Record<string, unknown> | null = null;
    try {
      parsedMetadata = metadataDraft.trim() ? tryParseJson(metadataDraft) : null;
    } catch (err) {
      setSaveStatus("error");
      setSaveMessage(
        err instanceof Error ? `Metadata error: ${err.message}` : "Metadata JSON invalid",
      );
      return;
    }
    const metadataToSave = (parsedMetadata
      ? { ...(parsedMetadata as Record<string, unknown>) }
      : {}) as Record<string, unknown> & { _revisions?: RevisionEntry[] };

    const revisionSummaryParts: string[] = [];
    if (metadataDraft.trim()) {
      revisionSummaryParts.push("metadata");
    }
    if (documentDraft !== (selectedRecord.document ?? "")) {
      revisionSummaryParts.push("document");
    }
    const revisionEntry: RevisionEntry = {
      timestamp: new Date().toISOString(),
      user: editorName,
      summary: revisionSummaryParts.length
        ? `Updated ${revisionSummaryParts.join(" & ")} via Memory Browser`
        : "Updated record via Memory Browser",
    };
    const existingRevisions = Array.isArray(metadataToSave._revisions)
      ? [...metadataToSave._revisions]
      : [];
    existingRevisions.push(revisionEntry);
    metadataToSave._revisions = existingRevisions;

    setSaveStatus("saving");
    setSaveMessage(null);
    try {
      const updated = await updateMemoryRecord(selectedCollection, selectedRecord.id, {
        metadata: metadataToSave,
        document: documentDraft,
      });
      setSelectedRecord(updated);
      setResults((prev) =>
        prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
      );
      setSaveStatus("success");
      setSaveMessage("Record updated.");
      window.setTimeout(() => {
        setSaveStatus("idle");
        setSaveMessage(null);
      }, 2500);
    } catch (err) {
      if (handleUpgradeError(err)) {
        return;
      }
      setSaveStatus("error");
      setSaveMessage(err instanceof Error ? err.message : "Failed to update record");
    }
  };

  const handlePromoteToCrm = async () => {
    if (!selectedCollection || !selectedRecord) return;
    if (!canUseMemoryPromotion) {
      openUpgradeModal();
      return;
    }
    setPromoteStatus("saving");
    setPromoteMessage(null);
    try {
      const updated = await promoteMemoryRecord(selectedCollection, selectedRecord.id, {
        status: "confirmed",
      });
      setSelectedRecord((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      setResults((prev) =>
        prev.map((item) => (item.id === updated.id ? { ...item, metadata: updated.metadata } : item)),
      );
      setPromoteStatus("success");
      setPromoteMessage("Promoted to CRM");
      window.setTimeout(() => {
        setPromoteStatus("idle");
        setPromoteMessage(null);
      }, 2500);
    } catch (err) {
      if (handleUpgradeError(err)) {
        setPromoteStatus("error");
        setPromoteMessage("Upgrade required");
        return;
      }
      setPromoteStatus("error");
      setPromoteMessage(err instanceof Error ? err.message : "Unable to promote record");
    }
  };

  const embeddingPreview = useMemo(() => {
    if (!selectedRecord?.embedding || !selectedRecord.embedding.length) {
      return null;
    }
    const sample = selectedRecord.embedding.slice(0, 12).map((value) => value?.toFixed?.(3) ?? value);
    return `${sample.join(", ")}${selectedRecord.embedding.length > 12 ? "…" : ""}`;
  }, [selectedRecord?.embedding]);

  const recordMetadata = (selectedRecord?.metadata ?? {}) as Record<string, unknown>;
  const embeddingDimension = selectedRecord?.embedding?.length ?? null;
  const createdAtLabel = useMemo(() => formatMetadataDate(recordMetadata), [recordMetadata]);
  const sourceLabel = useMemo(
    () =>
      (getMetadataValue(recordMetadata, [
        "source",
        "source_name",
        "ingestion_source",
        "artist_name",
        "origin",
      ]) as string | null) ?? "—",
    [recordMetadata],
  );
  const revisionEntries = useMemo<RevisionEntry[]>(() => {
    const raw = (recordMetadata as { _revisions?: unknown })._revisions;
    if (!Array.isArray(raw)) return [];
    return (raw as RevisionEntry[]).slice().reverse();
  }, [recordMetadata]);

  const content = (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-foreground">Chroma Memory Browser</h1>
        <p className="text-sm text-muted-foreground">
          Inspect every collection powering the agents workflow. Query by natural language, refine
          with metadata filters, and edit stored documents in place.
        </p>
      </header>
      <div className="rounded-2xl border border-border bg-background/70 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex min-w-[220px] flex-1 flex-col text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Collection
            <select
              className="mt-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              value={selectedCollection ?? ""}
              onChange={(event) =>
                setSelectedCollection(event.target.value ? event.target.value : null)
              }
              disabled={collectionsLoading || collections.length === 0}
            >
              <option value="">{collectionsLoading ? "Loading…" : "Select collection…"}</option>
              {collections.map((collection) => (
                <option key={collection.name} value={collection.name}>
                  {collection.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void loadCollections()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground/70 hover:text-foreground disabled:opacity-50"
            disabled={collectionsLoading}
          >
            <RefreshCw size={14} className={collectionsLoading ? "animate-spin" : ""} />
            Refresh list
          </button>
        </div>
        {collectionsError && (
          <p className="mt-2 text-sm text-rose-300">{collectionsError}</p>
        )}
        <div className="mt-4 space-y-1 text-xs text-muted-foreground">
          <p>
            How to use: choose a collection, decide whether to include semantic prompts and metadata
            filters (they are combined with <strong>AND</strong> logic), then run a search. You can
            also supply JSON metadata/doc filters or update selected records directly in the drawer.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Semantic prompt narrows results using embeddings when included.</li>
            <li>Metadata filters run server-side first; use preset rows or JSON for advanced logic.</li>
            <li>Document filters match on stored text, useful for keyword-only lookups.</li>
          </ul>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px,minmax(0,1fr)]">
        <aside className="rounded-2xl border border-border bg-background/60">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
            <Database size={16} />
            Collections
          </div>
          <div className="max-h-[28rem] overflow-y-auto">
            {collectionsLoading ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">Loading collections…</p>
            ) : collections.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">No collections detected.</p>
            ) : (
              <ul className="divide-y divide-border text-sm">
                {collections.map((collection) => {
                  const isActive = selectedCollection === collection.name;
                  return (
                    <li key={collection.name}>
                      <button
                        type="button"
                        onClick={() => setSelectedCollection(collection.name)}
                        className={`flex w-full flex-col items-start gap-1 px-4 py-3 text-left transition ${
                          isActive
                            ? "bg-background/50 text-foreground"
                            : "text-foreground/70 hover:bg-background/30"
                        }`}
                      >
                        <span className="font-semibold">{collection.name}</span>
                        <span className="text-xs text-muted-foreground/80">
                          {collection.metadata?.description || "No description"}
                        </span>
                        <span className="text-xs text-muted-foreground/70">
                          {collection.size != null ? `${collection.size} records` : "—"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        <CapabilityGate
          flag="canUseMemoryBrowser"
          fallback={<LockedFeatureCard flag="canUseMemoryBrowser" />}
        >
          <section className="space-y-6">
          <div className="rounded-2xl border border-border bg-background/70 p-4 shadow-inner shadow-black/10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Search memory</h2>
                <p className="text-xs text-muted-foreground">
                  Combine semantic search with structured metadata filters.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="uppercase tracking-wide"
                onClick={handleSearch}
                disabled={searching}
              >
                <Search size={16} />
                {searching ? "Searching…" : "Run search"}
              </Button>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                <label htmlFor="memory-semantic-query" className="text-xs font-semibold">
                  Natural language prompt
                </label>
                <p className="mt-1 text-[11px] text-muted-foreground/80 normal-case">
                  Combined with metadata filters (AND logic). Uncheck to exclude embeddings for the next
                  search.
                </p>
                <label className="mt-2 inline-flex items-center gap-2 text-[11px] normal-case text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={useSemanticQuery}
                    onChange={(event) => setUseSemanticQuery(event.target.checked)}
                    className="h-4 w-4 rounded border border-border bg-background/40 text-gold focus:ring-primary"
                  />
                  Include semantic prompt
                </label>
                <textarea
                  id="memory-semantic-query"
                  rows={3}
                  value={queryText}
                  onChange={(event) => setQueryText(event.target.value)}
                  className={`mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground ${
                    useSemanticQuery ? "" : "opacity-60"
                  }`}
                  placeholder='e.g. "venues similar to The Fillmore"'
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Page size
                  <input
                    type="number"
                    min={5}
                    max={100}
                    value={pageSize}
                    onChange={(event) => setPageSize(Math.max(5, Number(event.target.value) || 5))}
                    className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Include embeddings
                  <input
                    type="checkbox"
                    checked={includeEmbeddings}
                    onChange={(event) => setIncludeEmbeddings(event.target.checked)}
                    className="mt-2 h-5 w-5 rounded border border-border bg-background/40 text-gold focus:ring-primary"
                  />
                </label>
              </div>
            </div>
            {recentQueries.length > 0 && (
              <CapabilityGate flag="canUseMemoryBrowser" variant="hide">
                <div className="mt-4 flex flex-wrap items-end gap-3 text-xs text-muted-foreground">
                  <label className="flex flex-col text-xs uppercase tracking-wide">
                    Recent queries
                    <select
                      className="mt-2 min-w-[220px] rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                      value={selectedRecentId}
                      onChange={(event) => setSelectedRecentId(event.target.value)}
                    >
                      {recentQueries.map((recent) => (
                        <option key={recent.id} value={recent.id}>
                          {recent.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const saved =
                        recentQueries.find((item) => item.id === selectedRecentId) ?? recentQueries[0];
                      if (saved) {
                        restoreSavedQuery(saved);
                      }
                    }}
                    className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/70 hover:text-foreground"
                  >
                    Load query
                  </button>
                </div>
              </CapabilityGate>
            )}
            <CapabilityGate
              flag="canUseMemoryFiltersAdvanced"
              fallback={
                <LockedFeatureCard flag="canUseMemoryFiltersAdvanced" />
              }
            >
              <div className="mt-4 space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <span className="text-xs uppercase tracking-wide text-muted-foreground/80">Metadata filters</span>
                  <p className="text-[11px] text-muted-foreground/80 normal-case">
                    Toggled on, these filters AND together with the semantic prompt.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                  <label className="inline-flex items-center gap-2 normal-case">
                    <input
                      type="checkbox"
                      checked={useMetadataFilters}
                      onChange={(event) => setUseMetadataFilters(event.target.checked)}
                      className="h-4 w-4 rounded border border-border bg-background/40 text-gold focus:ring-primary"
                    />
                    Include metadata filters
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setFilterRows((prev) => [
                        ...prev,
                        {
                          id: generateRowId(),
                          field: "status",
                          value: "",
                        },
                      ]);
                    }}
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    + Add row
                  </button>
                </div>
              </div>
              <div className="grid gap-3 text-xs text-muted-foreground/80 md:grid-cols-3">
                <label className="space-y-1">
                  <span>Status presets</span>
                  <select
                    defaultValue=""
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value) {
                        handlePresetFilter("status", value);
                        event.target.value = "";
                      }
                    }}
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                  >
                    <option value="">Select status…</option>
                    {STATUS_PRESETS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span>Region presets</span>
                  <select
                    defaultValue=""
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value) {
                        handlePresetFilter("region", value);
                        event.target.value = "";
                      }
                    }}
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                  >
                    <option value="">Select region…</option>
                    {REGION_PRESETS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span>Tag presets</span>
                  <select
                    defaultValue=""
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value) {
                        handlePresetFilter("tags", value);
                        event.target.value = "";
                      }
                    }}
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                  >
                    <option value="">Select tag…</option>
                    {TAG_PRESETS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {filterRows.length > 0 ? (
                <FilterBuilder filters={filterRows} onChange={setFilterRows} />
              ) : (
                <div className="rounded-xl border border-border bg-background/60 p-4 text-sm text-muted-foreground">
                  <p>
                    Use structured filters for keys like <code className="text-foreground">status</code>,{" "}
                    <code className="text-foreground">state</code>, or <code className="text-foreground">tags</code>.
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setFilterRows([
                        {
                          id: generateRowId(),
                          field: "status",
                          value: "",
                        },
                      ])
                    }
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/70 hover:text-foreground"
                  >
                    <History size={14} />
                    Start a filter
                  </button>
                </div>
              )}
              </div>
            </CapabilityGate>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Metadata filter (JSON)
                <span className="block text-[11px] font-normal normal-case text-muted-foreground/80">
                  Applied only when metadata filters are included.
                </span>
                <textarea
                  rows={4}
                  value={metadataFilter}
                  onChange={(event) => setMetadataFilter(event.target.value)}
                  className={`mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground ${
                    useMetadataFilters ? "" : "opacity-60"
                  }`}
                  placeholder='{"state": "CO"}'
                />
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Document filter (JSON)
                <textarea
                  rows={4}
                  value={documentFilter}
                  onChange={(event) => setDocumentFilter(event.target.value)}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  placeholder='{"$contains": "The Fillmore"}'
                />
              </label>
            </div>
            {searchError && (
              <p className="mt-3 text-sm text-rose-300">
                {searchError}
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-background/60">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 text-sm">
              <div className="space-y-1">
                <span className="font-semibold text-foreground">
                  Results {results.length ? `(${results.length})` : ""}
                </span>
                <p className="text-xs text-muted-foreground/80">
                  Page {page} · Showing {results.length}{" "}
                  {totalResults != null ? `of ${totalResults}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => handleExport("json", results)}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-foreground/70 hover:text-foreground"
                  disabled={!results.length}
                >
                  <Download size={14} /> JSON
                </button>
                <button
                  type="button"
                  onClick={() => handleExport("csv", results)}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-foreground/70 hover:text-foreground"
                  disabled={!results.length}
                >
                  <Download size={14} /> CSV
                </button>
                {searching && (
                  <span className="text-xs text-muted-foreground">Searching collection…</span>
                )}
              </div>
            </div>
            {results.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground">
                Run a search to browse records in {selectedCollection || "Chroma"}.
              </p>
            ) : (
              <>
                <div className="space-y-3 md:hidden">
                  {results.map((record) => {
                    const isActive = selectedRecord?.id === record.id;
                    return (
                      <div
                        key={record.id}
                        className={`rounded-2xl border border-border bg-background/40 p-4 text-sm text-foreground/80 ${
                          isActive ? "border-primary/60 bg-primary/10" : ""
                        }`}
                        onClick={() => handleSelectRecord(record)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleSelectRecord(record);
                          }
                        }}
                      >
                        <p className="font-semibold text-foreground">{record.id}</p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {record.document
                            ? record.document.slice(0, 160) +
                              (record.document.length > 160 ? "…" : "")
                            : "—"}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground/80">
                          Score/Distance: {record.distance != null ? record.distance.toFixed(4) : "—"}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="hidden md:block max-h-[26rem] overflow-x-auto overflow-y-auto">
                  <table className="min-w-full divide-y divide-border text-sm">
                    <thead className="bg-background/40 text-xs uppercase tracking-wide text-muted-foreground/80">
                      <tr>
                        <th className="px-4 py-2 text-left">ID</th>
                        <th className="px-4 py-2 text-left">Document</th>
                        <th className="px-4 py-2 text-left">Score/Distance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {results.map((record) => {
                        const isActive = selectedRecord?.id === record.id;
                        return (
                          <tr
                            key={record.id}
                            className={`cursor-pointer transition ${
                              isActive ? "bg-background/50" : "hover:bg-background/30"
                            }`}
                            onClick={() => handleSelectRecord(record)}
                          >
                            <td className="px-4 py-2 font-medium text-foreground">{record.id}</td>
                            <td className="px-4 py-2 text-muted-foreground">
                              {record.document
                                ? record.document.slice(0, 120) +
                                  (record.document.length > 120 ? "…" : "")
                                : "—"}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground/80">
                              {record.distance != null ? record.distance.toFixed(4) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {results.length > 0 && (
              <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={() => {
                    if (page <= 1 || !lastPayload) return;
                    void runSearch({ ...lastPayload, page: page - 1 });
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-foreground/70 hover:text-foreground disabled:opacity-40"
                  disabled={page <= 1 || !lastPayload || searching}
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span>
                  Page {page}{" "}
                  {totalResults ? `of ${Math.max(1, Math.ceil(totalResults / pageSize))}` : ""}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (!lastPayload) return;
                    const totalPages =
                      totalResults != null ? Math.ceil(totalResults / pageSize) : null;
                    if (totalPages && page >= totalPages) return;
                    void runSearch({ ...lastPayload, page: page + 1 });
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-foreground/70 hover:text-foreground disabled:opacity-40"
                  disabled={searching || (!lastPayload ? true : false)}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>

          {selectedRecord && (
            <div className="rounded-2xl border border-border bg-background/70 p-4 shadow-inner shadow-black/10">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Record detail</h2>
                  <p className="text-xs text-muted-foreground">{selectedRecord.id}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <CapabilityGate flag="canUseMemoryEdit" variant="hide">
                    <Button
                      type="button"
                      variant="success"
                      size="sm"
                      className="uppercase tracking-wide"
                      onClick={handleSaveRecord}
                      disabled={saveStatus === "saving"}
                    >
                      <Save size={16} />
                      {saveStatus === "saving" ? "Saving…" : "Save changes"}
                    </Button>
                  </CapabilityGate>
                  <CapabilityGate flag="canUseMemoryPromotion" variant="hide">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="uppercase tracking-wide"
                      onClick={handlePromoteToCrm}
                      disabled={promoteStatus === "saving"}
                    >
                      <Share2 size={16} />
                      {promoteStatus === "saving" ? "Promoting…" : "Promote to CRM"}
                    </Button>
                  </CapabilityGate>
                </div>
              </div>
              {(saveMessage || promoteMessage) && (
                <div className="mt-2 space-y-1 text-sm">
                  {saveMessage && (
                    <p className={saveStatus === "success" ? "text-emerald-200" : "text-rose-300"}>
                      {saveMessage}
                    </p>
                  )}
                  {promoteMessage && (
                    <p className={promoteStatus === "success" ? "text-emerald-200" : "text-rose-300"}>
                      {promoteMessage}
                    </p>
                  )}
                </div>
              )}
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-border bg-background/60 p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground/80">Embedding dims</p>
                  <p className="mt-1 text-lg font-semibold text-foreground">
                    {embeddingDimension ?? "—"}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-background/60 p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground/80">Created</p>
                  <p className="mt-1 text-sm text-foreground">{createdAtLabel}</p>
                </div>
                <div className="rounded-lg border border-border bg-background/60 p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground/80">Source</p>
                  <p className="mt-1 text-sm text-foreground">{sourceLabel}</p>
                </div>
              </div>
              <CapabilityGate
                flag="canUseMemoryEdit"
                fallback={<LockedFeatureCard flag="canUseMemoryEdit" />}
              >
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Metadata (JSON)
                    <textarea
                      rows={10}
                      value={metadataDraft}
                      onChange={(event) => setMetadataDraft(event.target.value)}
                      className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                      placeholder="{}"
                    />
                  </label>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Document / Rationale
                    <textarea
                      rows={10}
                      value={documentDraft}
                      onChange={(event) => setDocumentDraft(event.target.value)}
                      className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                      placeholder="Document content"
                    />
                  </label>
                </div>
                {includeEmbeddings && (
                  <div className="mt-4 rounded-xl border border-border bg-background/80 p-3 text-xs text-muted-foreground">
                    <p className="font-semibold text-foreground">Embedding preview</p>
                    {embeddingPreview ? (
                      <>
                        <p className="mt-1 break-words text-muted-foreground/80">{embeddingPreview}</p>
                        <pre className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-background/80 p-2 text-[11px] text-muted-foreground">
                          {JSON.stringify(selectedRecord.embedding, null, 2)}
                        </pre>
                      </>
                    ) : (
                      <p className="mt-1 text-muted-foreground/80">Embedding not returned for this query.</p>
                    )}
                  </div>
                )}
                <div className="mt-6 grid gap-4 lg:grid-cols-2">
                  <div className="rounded-xl border border-border bg-background/60 p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-foreground">Change history</h3>
                      <span className="text-xs text-muted-foreground/80">
                        {revisionEntries.length ? `${revisionEntries.length} revisions` : "—"}
                      </span>
                    </div>
                    {revisionEntries.length === 0 ? (
                      <p className="mt-3 text-xs text-muted-foreground/80">No edits recorded yet.</p>
                    ) : (
                      <ul className="mt-3 space-y-3 text-xs text-muted-foreground">
                        {revisionEntries.slice(0, 5).map((revision, index) => (
                          <li
                            key={`${selectedRecord.id}-revision-${index}`}
                            className="rounded-lg border border-border bg-background/50 p-3"
                          >
                            <p className="font-semibold text-foreground">
                              {formatMaybeDateString(revision.timestamp) ?? "Unknown time"}
                            </p>
                            <p className="mt-1 text-[11px] text-muted-foreground/80">
                              {revision.summary ?? "Edited record"}
                              {revision.user ? ` • ${revision.user}` : ""}
                            </p>
                            {revision.changes && (
                              <pre className="mt-1 max-h-28 overflow-y-auto whitespace-pre-wrap rounded border border-border/60 bg-background/60 p-2 text-[11px] text-muted-foreground">
                                {JSON.stringify(revision.changes, null, 2)}
                              </pre>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <CapabilityGate
                    flag="canUseMemoryNeighbors"
                    fallback={<LockedFeatureCard flag="canUseMemoryNeighbors" />}
                  >
                    <div className="rounded-xl border border-border bg-background/60 p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-foreground">Similar records</h3>
                        {neighborsLoading && <span className="text-xs text-muted-foreground/80">Loading…</span>}
                      </div>
                      {neighborsError && (
                        <p className="mt-2 text-xs text-rose-300">{neighborsError}</p>
                      )}
                      {!neighborsLoading && neighbors.length === 0 ? (
                        <p className="mt-3 text-xs text-muted-foreground/80">No neighbors yet.</p>
                      ) : (
                        <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
                          {neighbors.map((neighbor) => {
                            const venueName =
                              (neighbor.metadata?.["venue"] as string | undefined) ??
                              (neighbor.metadata?.["name"] as string | undefined) ??
                              neighbor.id;
                            const cityName =
                              (neighbor.metadata?.["city"] as string | undefined) ?? "Unknown city";
                            return (
                              <li
                                key={neighbor.id}
                                className="rounded-lg border border-border bg-background/50 p-3"
                              >
                                <div className="flex items-center justify-between">
                                  <button
                                    type="button"
                                    onClick={() => handleSelectRecord(neighbor)}
                                    className="text-left text-sm font-semibold text-foreground hover:underline"
                                  >
                                    {venueName}
                                  </button>
                                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
                                    {neighbor.distance != null ? neighbor.distance.toFixed(3) : "—"}
                                  </span>
                                </div>
                                <p className="mt-1 text-[11px] text-muted-foreground/80">{cityName}</p>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </CapabilityGate>
                </div>
              </CapabilityGate>
            </div>
          )}
          </section>
        </CapabilityGate>
      </div>
    </div>
  );

  return content;
}

function handleExport(format: "json" | "csv", data?: MemoryRecord[]) {
  if (!data || data.length === 0) return;
  if (format === "json") {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    triggerDownload(blob, "memory-results.json");
  } else {
    const headers = ["id", "distance", "document", "metadata"];
    const csvRows = [
      headers.join(","),
      ...data.map((record) => {
        const document = record.document
          ? JSON.stringify(record.document).replace(/"/g, '""')
          : "";
        const metadata = record.metadata ? JSON.stringify(record.metadata).replace(/"/g, '""') : "";
        return [
          record.id,
          record.distance ?? "",
          `"${document}"`,
          `"${metadata}"`,
        ].join(",");
      }),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    triggerDownload(blob, "memory-results.csv");
  }
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function generateRowId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getMetadataValue(metadata: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (key in metadata && metadata[key] != null) {
      return metadata[key];
    }
  }
  return undefined;
}

function formatMaybeDateString(value: unknown): string | null {
  if (value == null) return null;
  const str = String(value);
  const timestamp = Date.parse(str);
  if (Number.isNaN(timestamp)) {
    return str;
  }
  return new Date(timestamp).toLocaleString();
}

function formatMetadataDate(metadata: Record<string, unknown>): string {
  const raw = getMetadataValue(metadata, [
    "created_at",
    "createdAt",
    "timestamp",
    "submitted_at",
    "ingested_at",
    "last_updated",
  ]);
  return formatMaybeDateString(raw) ?? "—";
}

type MaybeUpgradeError = { payload?: Record<string, unknown> };

function isRequiresProError(err: unknown): boolean {
  const payload = (err as MaybeUpgradeError | undefined)?.payload;
  if (!payload) {
    return false;
  }
  const directError = typeof payload.error === "string" ? payload.error : undefined;
  const code = typeof payload.code === "string" ? payload.code : undefined;
  let detail: string | undefined;
  if (typeof payload.detail === "string") {
    detail = payload.detail;
  } else if (
    payload.detail &&
    typeof payload.detail === "object" &&
    "message" in payload.detail &&
    typeof (payload.detail as Record<string, unknown>).message === "string"
  ) {
    detail = String((payload.detail as Record<string, unknown>).message);
  }
  return directError === "requires_pro" || code === "requires_pro" || detail === "requires_pro";
}
